/**
 * Workstream Y.1a — paneStore
 *
 * Source-of-truth: backend `pane_manager` (per-tenant). The store mirrors
 * the backend list and exposes spawn/kill/refresh actions. Backend's
 * `name` is the unique key — frontend never invents names.
 *
 * Persistence model: backend is canonical. localStorage caches the last
 * fetched list for instant first-render; on mount we re-fetch and replace.
 *
 * The 7 ALLOWED_AGENT_TYPES match the backend's pane_manager.py constant —
 * any drift fails the spawn route with 400. (Y.1b added shell + codex on
 * top of Y.1a's 5; future agent types stay in lockstep with the backend.)
 */
import { create } from 'zustand';
import { api } from '@/services/api';

export const ALLOWED_AGENT_TYPES = [
  'openclaw_shell',
  'opencode_shell',
  // 2026-10-21: removed 'goose_shell' — goose deprecated (D3).
  'hermes_agent',
  // Y.1b — `shell` reuses the openclaw container's ttyd (bash -l per WS) with
  // no auto-launch (raw bash). 2026-05-08: removed `codex` — the codex CLI
  // is NOT installed in the openclaw image (verified: `which codex` empty).
  'shell',
  // Plan E (2026-05-08): `pi_shell` resolves to t440-pi-shell-{tenant},
  // a dedicated container for `@earendil-works/pi-coding-agent`. Container
  // entrypoint auto-launches Pi; AUTO_LAUNCH_COMMAND is empty.
  'pi_shell',
] as const;

export type AgentType = (typeof ALLOWED_AGENT_TYPES)[number];

export interface PaneRecord {
  name: string; // e.g. "openclaw_shell-alpha"
  agent_type: AgentType;
  started_at: number;
  last_activity: number;
  state: 'pending' | 'open' | 'closed';
  auto_launch: boolean; // Y.1b — false = raw bash, no agent injected on spawn
  // Y.3 — user-set rename overlay; null = use NATO name. Z's voice dispatcher
  // resolves "send X to refactor-bot" against display_name OR name.
  display_name: string | null;
}

/** Y.2 — backend-supplied cap policy. SPA disables spawn at cap. */
export interface PaneLimits {
  max_panes_per_tenant: number;
  max_panes_per_agent_type: number;
  hard_per_type_cap: number;
  idle_timeout_seconds: number;
}

interface PaneState {
  panes: PaneRecord[];
  limits: PaneLimits | null;
  isLoading: boolean;
  error: string | null;

  refresh: () => Promise<void>;
  refreshLimits: () => Promise<void>;
  spawn: (agentType: AgentType, autoLaunch?: boolean) => Promise<PaneRecord>;
  kill: (name: string) => Promise<void>;
  /** Y.1b — type text into a pane's PTY (also surfaces the API used by Workstream Z). */
  sendInput: (name: string, text: string) => Promise<void>;
  /** Y.3 — set or clear display_name overlay; null clears. 409 on collision. */
  rename: (name: string, displayName: string | null) => Promise<PaneRecord>;
}

const LS_CACHE_KEY = 'vibe_pane_cache';

function readCache(): PaneRecord[] {
  try {
    const raw = localStorage.getItem(LS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCache(panes: PaneRecord[]): void {
  try {
    localStorage.setItem(LS_CACHE_KEY, JSON.stringify(panes));
  } catch {
    // localStorage quota / disabled — non-fatal; backend re-fetch is canonical.
  }
}

export const usePaneStore = create<PaneState>((set, get) => ({
  panes: readCache(),
  limits: null,
  isLoading: false,
  error: null,

  async refresh() {
    set({ isLoading: true, error: null });
    try {
      const panes = await api.get<PaneRecord[]>('/terminals');
      writeCache(panes);
      set({ panes, isLoading: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      set({ isLoading: false, error: msg });
      throw err;
    }
  },

  async refreshLimits() {
    // Y.2 — fetch the cap policy from /api/terminals/limits. Called once on
    // Terminals view mount; the values are stable across the session.
    // Failure is non-fatal: SPA can still attempt spawns and surface the 409
    // from the backend if a cap is hit.
    try {
      const limits = await api.get<PaneLimits>('/terminals/limits');
      set({ limits });
    } catch (err) {
      // Limits unavailable — UI falls back to "spawn always allowed; backend
      // will 409 at cap" behavior. Not user-visible. Log via console.warn so
      // failures are diagnosable in browser devtools without raising.
      console.warn(
        '[paneStore] refreshLimits failed; falling back to backend 409 enforcement:',
        err,
      );
    }
  },

  async spawn(agentType, autoLaunch = true) {
    set({ error: null });
    try {
      const body: { agent_type: AgentType; auto_launch?: boolean } = {
        agent_type: agentType,
      };
      // Only send auto_launch when overriding the default to keep the
      // request body minimal + match the backend SpawnRequest default.
      if (autoLaunch === false) body.auto_launch = false;
      const created = await api.post<PaneRecord>('/terminals', body);
      // Optimistically append; refresh will reconcile.
      const next = [...get().panes, created];
      writeCache(next);
      set({ panes: next });
      return created;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      set({ error: msg });
      throw err;
    }
  },

  async kill(name) {
    set({ error: null });
    try {
      await api.delete(`/terminals/${encodeURIComponent(name)}`);
      const next = get().panes.filter((p) => p.name !== name);
      writeCache(next);
      set({ panes: next });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      set({ error: msg });
      throw err;
    }
  },

  async sendInput(name, text) {
    // Y.1b — backend writes the text to the pane's PTY via input_pump.
    // No state change locally; the upstream WS will echo the typed text
    // back as OUTPUT frames which the xterm renders normally.
    await api.post(`/terminals/${encodeURIComponent(name)}/input`, { text });
  },

  async rename(name, displayName) {
    set({ error: null });
    try {
      const updated = await api.patch<PaneRecord>(
        `/terminals/${encodeURIComponent(name)}`,
        { display_name: displayName },
      );
      const next = get().panes.map((p) => (p.name === name ? updated : p));
      writeCache(next);
      set({ panes: next });
      return updated;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      set({ error: msg });
      throw err;
    }
  },
}));
